"use client";

import { useEffect, useRef } from "react";
import { X } from "lucide-react";

interface DeleteConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  contentType: "post" | "reply";
}

/**
 * Confirmation modal for deleting posts or replies
 * Shows appropriate text based on content type
 */
export function DeleteConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  contentType,
}: DeleteConfirmModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);

  // Store the element that had focus before modal opened
  useEffect(() => {
    if (isOpen) {
      previousActiveElementRef.current = document.activeElement as HTMLElement;
      // Focus the cancel button when modal opens (safer default than delete)
      setTimeout(() => {
        cancelButtonRef.current?.focus();
      }, 0);
    } else {
      // Return focus to the element that opened the modal
      if (previousActiveElementRef.current) {
        previousActiveElementRef.current.focus();
      }
    }
  }, [isOpen]);

  // Handle Escape key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  // Focus trap: keep focus within modal
  useEffect(() => {
    if (!isOpen) return;

    const handleTab = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;

      const focusableElements = modalRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener("keydown", handleTab);
    return () => {
      document.removeEventListener("keydown", handleTab);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const isPost = contentType === "post";
  const title = isPost ? "Delete this post?" : "Delete this reply?";
  const description = isPost
    ? "This action cannot be undone. The post will be removed from view, but replies will remain visible."
    : "This action cannot be undone. The reply will be removed from view.";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-modal-title"
      aria-describedby="delete-modal-description"
      onClick={(e) => {
        // Close modal when clicking backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={modalRef}
        className="w-full max-w-md rounded-[18px] border border-[rgba(255,255,255,0.04)] bg-gradient-to-br from-[#171727] via-[#111118] to-[#171727] p-5 shadow-[0_24px_60px_rgba(0,0,0,0.75)]"
      >
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2
              id="delete-modal-title"
              className="text-xl font-semibold text-[#f7f7ff]"
            >
              {title}
            </h2>
            <p
              id="delete-modal-description"
              className="mt-2 text-sm text-[#a3a3c2]"
            >
              {description}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg border border-[#262636] bg-[#0a0a12] p-2 text-[#a3a3c2] transition hover:border-[rgba(255,255,255,0.16)] hover:bg-[#141422]"
            aria-label="Close delete confirmation"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex gap-3">
          <button
            ref={cancelButtonRef}
            type="button"
            onClick={onClose}
            className="flex-1 rounded-full border border-[#262636] bg-[#0a0a12] px-4 py-2 text-sm font-semibold text-[#a3a3c2] transition hover:border-[rgba(255,255,255,0.16)]"
          >
            Cancel
          </button>
          <button
            ref={deleteButtonRef}
            type="button"
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className="flex-1 rounded-full bg-gradient-to-r from-[#ff3ea5] to-[#ff6ac2] px-4 py-2 text-sm font-semibold text-[#160513] shadow-[0_12px_36px_rgba(255,62,165,0.4)] transition hover:brightness-[1.05]"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

